import React, { FC, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';

import { ISystemSettingsDTO } from 'src/api/dtos';
import { CheckFilledIcon, LogoutIcon, Modal } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { refreshToken } from 'src/shared/utils';
import { RootState } from 'src/store/types';

const DEFAULT_TIMEOUT_TIME = 1000 * 60 * 20; // 20 minutes in milliseconds
const LOGOUT_TIME = 30; // 30 seconds

interface Props {
  settings: ISystemSettingsDTO | null;
  onSignOut: () => void;
}

export const AutoIdleTimer: FC<Props> = ({ settings, onSignOut }) => {
  const { t } = useTranslate();
  const timer = useRef<number | null>(null);
  const { authenticated } = useSelector((state: RootState) => state.user);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(LOGOUT_TIME);

  const timeoutTime = settings?.appIdleTimeoutSeconds ? settings.appIdleTimeoutSeconds * 1000 : DEFAULT_TIMEOUT_TIME;

  const handleIdle = () => {
    setShowModal(true);
  };

  const idleTimer = useIdleTimer({
    element: document,
    debounce: 250,
    timeout: timeoutTime,
    onIdle: handleIdle,
    crossTab: true,
  });

  const handleSignOut = () => {
    setShowModal(false);
    onSignOut();
  };

  const handleClose = async () => {
    idleTimer?.reset();
    setShowModal(false);
    await refreshToken();
  };

  useEffect(() => {
    if (!showModal) {
      setCounter(LOGOUT_TIME);
      return;
    }

    if (counter > 0) {
      timer.current = setTimeout(() => setCounter(counter - 1), 1000) as any;
    } else {
      onSignOut();
    }

    return () => {
      if (!timer?.current) {
        return;
      }

      clearTimeout(timer.current);
    };
  }, [showModal, counter, timer]);

  if (!settings || !authenticated) {
    return null;
  }

  return (
    <>
      <Modal
        open={showModal}
        onClose={handleClose}
        modalHeaderProps={{
          title: t('timeout.youHaveBeenIdleForXMinutes', (timeoutTime / 60000).toFixed(0)),
          onClose: handleClose,
        }}
        modalFooterProps={{
          closeText: t('global.logout'),
          submitText: t('timeout.staySignedIn'),
          closeIcon: <LogoutIcon />,
          submitIcon: <CheckFilledIcon />,
          onClose: handleSignOut,
          onSubmit: handleClose,
        }}
      >
        <p className="mt-4">{t('timeout.youWillBeLoggedOutInXSeconds', counter)}</p>
      </Modal>
    </>
  );
};
